// Number, date and axis formatting. Indian digit grouping (en-IN) throughout.

const intFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const decFmt = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
// Rates read to one decimal (29.7%), as in the product mockup.
const pctFmt = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const compactFmt = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function fmt(value, format = "int") {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  switch (format) {
    case "int":
      return Number.isFinite(n) ? intFmt.format(n) : String(value);
    case "dec":
      return Number.isFinite(n) ? decFmt.format(n) : String(value);
    case "pct":
      return Number.isFinite(n) ? `${pctFmt.format(n)}%` : String(value);
    case "duration":
      return fmtDuration(n);
    case "date":
      return fmtDate(value);
    case "datetime":
      return fmtDateTime(value);
    default:
      return String(value);
  }
}

export function fmtCompact(n) {
  return Number.isFinite(Number(n)) ? compactFmt.format(Number(n)) : "—";
}

export function fmtDuration(seconds) {
  if (!Number.isFinite(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return [h, m, r].map((x) => String(x).padStart(2, "0")).join(":");
}

export function fmtDate(value) {
  const s = String(value ?? "");
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m || m[1] === "1970") return s ? "—" : "";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

export function fmtDateTime(value) {
  const s = String(value ?? "");
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (!m) return fmtDate(s);
  return `${fmtDate(s)}, ${m[4]}:${m[5]}`;
}

/** Axis tick label for an x value. */
export function fmtAxis(value, xFormat) {
  const s = String(value);
  if (xFormat === "day") {
    const m = /^\d{4}-(\d{2})-(\d{2})/.exec(s);
    return m ? `${Number(m[2])} ${MONTHS[Number(m[1]) - 1]}` : s;
  }
  if (xFormat === "month") {
    const m = /^(\d{4})-(\d{2})/.exec(s);
    return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1].slice(2)}` : s;
  }
  if (xFormat === "hour") {
    const h = Number(s);
    if (!Number.isFinite(h)) return s;
    if (h === 0) return "12a";
    if (h === 12) return "12p";
    return h < 12 ? `${h}a` : `${h - 12}p`;
  }
  if (xFormat === "videoPct") return `${s}%`;
  return s;
}

/** Long label for tooltips. */
export function fmtX(value, xFormat) {
  if (xFormat === "day") return fmtDate(value);
  if (xFormat === "month") {
    const m = /^(\d{4})-(\d{2})/.exec(String(value));
    return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : String(value);
  }
  if (xFormat === "hour") {
    const h = Number(value);
    return `${String(h).padStart(2, "0")}:00–${String(h).padStart(2, "0")}:59 IST`;
  }
  if (xFormat === "videoPct") return `${value}% into the video`;
  return String(value);
}

/** Percent change, or null when there is no meaningful base. */
export function delta(value, prev) {
  if (prev === undefined || prev === null || !Number.isFinite(prev) || prev === 0) return null;
  return ((value - prev) / Math.abs(prev)) * 100;
}
